import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchLevels, fetchStudents } from "../student/studentSlice";
import apiClient from "../../services/apiClient";

import Modal from "../../components/Modal";
import ReviewSettingsPanel from "../../components/ReviewSettingsPanel";
import StepProgressBar from "../../components/StepProgressBar";
import DateSelectionPage from "../worksheets/components/WorksheetModal/DateSelectionPage/DateSelectionPage";
import StudentSelectionPage from "../worksheets/components/WorksheetModal/StudentSelectionPage/StudentSelectionPage";

import { useDefaultRewardPoints } from "../reward/RewardPointsField";

import "../../styles/WorksheetModalV2.css";

/**
 * 문제뱅크 시험지를 학생에게 출제하는 창.
 *
 * **지인에듀 문제지 출제 창과 같은 부품을 쓴다**(`WorksheetModal` 의 마감일·학생
 * 선택 페이지를 그대로 가져온다). 문제뱅크만 다른 화면을 쓰면 같은 일을 두 가지
 * 방식으로 하게 되어, 교사가 화면마다 다시 배워야 한다.
 *
 * 단계는 둘뿐이다. 제목·범위는 문제뱅크 탭에서 시험지를 만들 때 이미 정해졌다.
 * 출제하면 보통 과제가 만들어진다(`api/echo/bank_to_assignment.py`).
 */
// 지인에듀 출제 창과 같은 순서: 제목 → 유형(복습) → 마감일 → 학생
const STEPS = [
  { key: "title", label: "제목" },
  { key: "retry", label: "유형" },
  { key: "date", label: "마감일" },
  { key: "student", label: "학생" },
];

const BankAssignModal = ({ bankSet, onClose, onDone }) => {
  const dispatch = useDispatch();
  const { allStudents } = useSelector((state) => state.student);

  const [currentStep, setCurrentStep] = useState("title");
  const [title, setTitle] = useState("");
  useEffect(() => {
    setTitle(bankSet?.title || "");
  }, [bankSet]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [hideAfterClosed, setHideAfterClosed] = useState(false);
  // 복습 설정 (지인에듀 출제와 같은 세 값: 사용 여부·자동/수동·최대 횟수)
  const [retryEnabled, setRetryEnabled] = useState(true);
  const [autoRetryEnabled, setAutoRetryEnabled] = useState(true);
  const [maxRetries, setMaxRetries] = useState(3);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [busy, setBusy] = useState(false);
  // 완료 포인트. 문제뱅크 단어는 단어테스트(vocabulary)로, 나머지는 문제뱅크(bank)로 나가므로
  // 서버가 기억하는 유형도 그 둘이다(api/echo/bank_to_assignment.py).
  const [rewardPoints, setRewardPoints] = useState(100);
  useDefaultRewardPoints(bankSet ? (bankSet.kind === "vocab" ? "vocabulary" : "bank") : "", setRewardPoints);

  useEffect(() => {
    if (allStudents.length === 0) {
      dispatch(fetchStudents());
      dispatch(fetchLevels());
    }
  }, [dispatch, allStudents.length]);

  if (!bankSet) return null;

  const localDate = (date) => {
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const assign = () => {
    if (selectedStudents.length === 0) {
      alert("학생을 골라 주세요");
      return;
    }
    if (busy) return;
    setBusy(true);
    apiClient
      .post("/api/v1/echo/bank/assign/", {
        t: bankSet.token,
        title: title.trim(),
        student_ids: selectedStudents.map((one) => one.id),
        due_date: localDate(selectedDate),
        hide_after_closed: hideAfterClosed,
        is_retry_enabled: retryEnabled,
        is_auto_retry_enabled: retryEnabled && autoRetryEnabled,
        max_retries: maxRetries,
        reward_points: rewardPoints,
      }, { withAuth: true })
      .then((response) => response.data)
      .then((response) => {
        setBusy(false);
        if (!response.ok) {
          alert(response.error || "출제하지 못했어요");
          return;
        }
        alert(`${selectedStudents.length}명에게 출제했어요. 출제 문제지 목록에서 볼 수 있어요.`);
        onDone && onDone();
        onClose();
      })
      .catch(() => {
        setBusy(false);
        alert("출제하지 못했어요");
      });
  };

  return (
    <Modal>
      {/* 스타일은 ProblemBankPage.css 의 .problem-bank-assign-shell.
          인라인 vh 는 iOS 에서 주소창을 무시해 바닥 버튼이 잘렸다 (dvh 필요). */}
      <div className="problem-bank-assign-shell">
        <StepProgressBar
          steps={STEPS}
          currentStep={currentStep}
          onStepClick={(stepKey) => {
            const order = STEPS.map((one) => one.key);
            if (order.indexOf(stepKey) < order.indexOf(currentStep)) {
              setCurrentStep(stepKey);
            }
          }}
        />
        {currentStep === "title" && (
          <div
            style={{
              position: "relative", width: "100%", display: "flex",
              flexDirection: "column", minHeight: 0,
              maxHeight: "calc(80vh - 62px)", overflow: "hidden",
            }}
          >
            <div style={{ padding: "26px 22px 8px" }}>
              <div className="ws-modal-title">과제 제목 설정</div>
              <p className="ws-modal-sub" style={{ marginBottom: "18px" }}>
                학생 화면에 보여질 이름입니다.
              </p>
              <input
                type="text"
                placeholder="과제 제목을 입력해주세요."
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && title.trim() && setCurrentStep("retry")}
                autoFocus
                style={{
                  fontSize: "0.95rem", width: "100%", padding: "13px 16px",
                  border: "1.5px solid #E6EDEC", borderRadius: "12px",
                  boxSizing: "border-box", fontFamily: "inherit",
                  letterSpacing: "-0.01em", outline: "none",
                }}
              />
            </div>
            <div className="ws-bottom-bar" style={{ marginTop: "26px" }}>
              <button
                className="ws-btn-next"
                onClick={() => {
                  if (!title.trim()) return alert("과제 제목을 입력해주세요.");
                  setCurrentStep("retry");
                }}
              >
                다음
              </button>
            </div>
          </div>
        )}
        {currentStep === "retry" && (
          <div
            style={{
              position: "relative", width: "100%", display: "flex",
              flexDirection: "column", minHeight: 0,
              maxHeight: "calc(80vh - 62px)", overflow: "hidden",
            }}
          >
            <div style={{ padding: "26px 22px 8px", overflowY: "auto" }}>
              <div className="ws-modal-title">복습 설정</div>
              <p className="ws-modal-sub" style={{ marginBottom: "14px" }}>
                틀린 문제를 어떻게 다시 풀릴지 정합니다.
              </p>
              <ReviewSettingsPanel
                isRetryEnabled={retryEnabled}
                onToggleRetry={() => setRetryEnabled(!retryEnabled)}
                isAutoRetryEnabled={autoRetryEnabled}
                onSetAutoRetry={setAutoRetryEnabled}
                maxRetries={maxRetries}
                onSetMaxRetries={setMaxRetries}
              />
            </div>
            <div className="ws-bottom-bar" style={{ marginTop: "18px", justifyContent: "space-between" }}>
              <button className="ws-btn-prev" onClick={() => setCurrentStep("title")}>
                이전
              </button>
              <button className="ws-btn-next" onClick={() => setCurrentStep("date")}>
                다음
              </button>
            </div>
          </div>
        )}
        {currentStep === "date" && (
          <DateSelectionPage
            onClose={onClose}
            onClickNext={() => setCurrentStep("student")}
            selectedDate={selectedDate}
            handleDateSelect={setSelectedDate}
            hideAfterClosed={hideAfterClosed}
            setHideAfterClosed={setHideAfterClosed}
            rewardPoints={rewardPoints}
            setRewardPoints={setRewardPoints}
          />
        )}
        {currentStep === "student" && (
          <StudentSelectionPage
            onClose={onClose}
            onClickPrev={() => setCurrentStep("date")}
            handleAddStudents={assign}
            selectedStudents={selectedStudents}
            setSelectedStudents={setSelectedStudents}
            selectedDate={selectedDate}
          />
        )}
      </div>
    </Modal>
  );
};

export default BankAssignModal;
